import React, { useRef, useState } from 'react';
import { Epoch } from '../../types/epochs';
import { EpochItem } from './EpochItem';
import { useEpochCalculations } from '../../hooks/useEpochCalculations';
import useWindowDimensions from '../../hooks/useWindowDimentions';

interface EpochsContainerProps {
  epochs: Epoch[];
  epochRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  epochHeights: { [key: string]: number };
  setHoveredEpoch: (epoch: string | null) => void;
  onClose?: () => void;
}

export const EpochsContainer: React.FC<EpochsContainerProps> = ({
  epochs,
  epochRefs,
  epochHeights,
  setHoveredEpoch,
  onClose
}) => {
  const { calculateVerticalPosition, calculatePositions } = useEpochCalculations(epochs, epochHeights);
  const currentYear = new Date().getFullYear();
  const containerRef = useRef<HTMLDivElement>(null);
  const windowDimensions = useWindowDimensions();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [lastTouchY, setLastTouchY] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (windowDimensions.width > 768) return;
    
    setTouchStart(e.touches[0].clientY);
    setLastTouchY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !lastTouchY || windowDimensions.width > 768) return;

    const touchY = e.touches[0].clientY;
    const currentDiff = lastTouchY - touchY;
    
    const container = containerRef.current;
    
    if (container) {
      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtTop = container.scrollTop === 0;
      const isAtBottom = Math.abs(
        container.scrollHeight - container.clientHeight - container.scrollTop
      ) < 1;
      
      if (isScrollable && 
          ((currentDiff > 0 && !isAtBottom) || (currentDiff < 0 && !isAtTop))) {
        e.stopPropagation();
      }

      if ((isAtTop && currentDiff < 0) || (isAtBottom && currentDiff > 0)) {
        e.preventDefault();
      }
    }

    setLastTouchY(touchY);
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    setLastTouchY(null);
  };

  return (
    <div 
      className="epochs-container"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {epochs.map((epoch, index) => (
        <EpochItem
          key={epoch.title}
          epoch={epoch}
          index={index}
          currentYear={currentYear}
          epochRefs={epochRefs}
          calculateVerticalPosition={calculateVerticalPosition}
          calculatePositions={calculatePositions}
          setHoveredEpoch={setHoveredEpoch}
          onClose={onClose}
        />
      ))}
    </div>
  );
}; 